import React, { useState, useEffect } from 'react';
import { system } from '../services/api';
import { Info } from 'lucide-react';

/**
 * Subtle version display component
 * Shows a small icon in the bottom-right corner that reveals version info on hover
 */
function VersionFooter() {
  const [backendVersion, setBackendVersion] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [backendEnv, setBackendEnv] = useState(null);

  // Frontend version injected at build time
  const frontendVersion = process.env.REACT_APP_VERSION || 'dev';

  useEffect(() => {
    // Fetch backend version on mount
    system.getVersion().then(data => {
      if (data) {
        setBackendVersion(data.version);
        setBackendEnv(data.environment);
      }
    });
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 12,
        right: 12,
        zIndex: 1000,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Tooltip */}
      {isHovered && (
        <div
          role="tooltip"
          id="version-tooltip"
          style={{
            position: 'absolute',
            bottom: 32,
            right: 0,
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-primary)',
            borderRadius: 8,
            padding: '10px 14px',
            minWidth: 160,
            boxShadow: 'var(--shadow-md)',
            animation: 'fadeIn 0.15s ease-out',
          }}
        >
          <div style={{ 
            fontSize: 10, 
            textTransform: 'uppercase', 
            letterSpacing: '0.5px',
            color: 'var(--text-muted)',
            marginBottom: 8,
            fontWeight: 600,
          }}>
            Version Info
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: 4,
          }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Frontend</span>
            <span style={{ 
              fontSize: 12, 
              fontFamily: 'monospace',
              color: 'var(--text-primary)',
              fontWeight: 500,
            }}>
              v{frontendVersion}
            </span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
          }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Backend</span>
            <span style={{ 
              fontSize: 12, 
              fontFamily: 'monospace',
              color: backendVersion ? 'var(--text-primary)' : 'var(--text-muted)',
              fontWeight: 500,
            }}>
              {backendVersion ? `v${backendVersion}` : '...'}
            </span>
          </div>
          {backendEnv && backendEnv !== 'production' && (
            <div style={{
              marginTop: 8,
              paddingTop: 8,
              borderTop: '1px solid var(--border-primary)',
              fontSize: 10,
              color: 'var(--accent-warning)',
              textAlign: 'center',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              {backendEnv}
            </div>
          )}
        </div>
      )}

      {/* Icon button */}
      <button
        style={{
          width: 28,
          height: 28,
          borderRadius: 6,
          border: 'none',
          background: isHovered ? 'var(--bg-tertiary)' : 'transparent',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: isHovered ? 0.9 : 0.3,
          transition: 'opacity 0.2s, background 0.2s',
        }}
        title="Version info"
        aria-label="Show version information"
        aria-expanded={isHovered}
        aria-describedby={isHovered ? 'version-tooltip' : undefined}
        onClick={() => setIsHovered(!isHovered)}
      >
        <Info size={14} aria-hidden="true" />
      </button>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default VersionFooter;
